import type { Permission, Role } from '@sentra/contracts';
import { roleHasPermission } from '@sentra/contracts';
import type { LucideIcon } from 'lucide-react';
import { LayoutDashboard, ScanLine, Settings, Users } from 'lucide-react';

import type { IdleClass } from '@/features/auth/idle';
import type { UiCatalogue } from '@/i18n/es';

/**
 * The sections of this application, and what each one is.
 *
 * **One table, read by three things**: the sidebar, the command palette and the router. A section
 * added to a sidebar and forgotten in a palette is the shape that bug takes, and it is invisible to
 * anybody who uses the sidebar.
 *
 * **`idle` is declared per section, matching the server's own route registry.** AD-54's five
 * minutes belong to the gate screens and sixty to the administration ones, and the server decides
 * the same thing per API route in `packages/server/src/auth/matrix.ts`. The two tables are about
 * different things — one is a screen, the other is an endpoint — so they are not derived from each
 * other; what makes them agree is that a screen's calls are the routes of its own class, and a
 * disagreement costs a lock a few minutes early, never late.
 *
 * **A section is offered by the VERB its screen exists to exercise, never by rank.** The gate is
 * `presence:submit`, the register `person:read`, the administration section `analytics:read`
 * (its first screen is the dashboard). Rank used to stand in for all of it, which held only while
 * every role below an administrator was a desk: an analyst ranks above an operator and works no
 * door, so "at least an operator" offered it the gate. The grant table is the one place a role's
 * reach is written, and this reads it.
 *
 * **Hiding is not the control; {@link sectionRefusedAt} is the browser's half of it.** Every route
 * is authorized at the server, twice (AD-36), and the shell renders a refusal in place of a section
 * a typed URL reached, so a screen never mounts only to have its first request refused.
 */

type SectionId = keyof UiCatalogue['nav'];

export interface Section {
  readonly id: SectionId;
  /** The browser path. English and stable: a URL is quoted in a ticket and must not move. */
  readonly path: string;
  readonly icon: LucideIcon;
  readonly idle: IdleClass;
  /** The verb the section's screen exists to exercise; holding it is what offers the section. */
  readonly permission: Permission;
}

const SECTIONS: readonly Section[] = [
  /**
   * The landing route is a GATE screen, not an administration one, and the reason is that it is
   * the screen a terminal sits on when nobody is using it. It also issues no request of its own, so
   * the server's `AUTH_SESSION_IDLE_LOCKED` — which is what backs up every other window — never
   * fires here. Sixty minutes with no backstop on the default route is a shift attributed to
   * whoever signed in that morning, which is the failure AD-54 exists to prevent.
   */
  { id: 'overview', path: '/', icon: LayoutDashboard, idle: 'gate', permission: 'presence:read' },
  { id: 'scan', path: '/scan', icon: ScanLine, idle: 'gate', permission: 'presence:submit' },
  { id: 'persons', path: '/persons', icon: Users, idle: 'gate', permission: 'person:read' },
  { id: 'admin', path: '/admin', icon: Settings, idle: 'admin', permission: 'analytics:read' },
];

/** Whether a role reaches a section: whether it holds the verb the section exists for. */
function reaches(role: Role, section: Section): boolean {
  return roleHasPermission(role, section.permission);
}

/** The sections one role is offered. */
export function sectionsFor(role: Role): readonly Section[] {
  return SECTIONS.filter((section) => reaches(role, section));
}

/**
 * The section a path is inside, longest match first.
 *
 * Longest first because `/` is a prefix of everything: matching in declaration order would put
 * every screen in the product on the overview's sixty-minute window, including the gate.
 */
function sectionOf(pathname: string): Section | null {
  const matches = SECTIONS.filter(
    (section) => pathname === section.path || pathname.startsWith(`${section.path}/`),
  );
  return matches.reduce<Section | null>(
    (best, section) => (best === null || section.path.length > best.path.length ? section : best),
    null,
  );
}

/**
 * The section a path is inside when this role may NOT open it, or `null` when it may.
 *
 * `null` also for a path inside no section at all: react-router sends one to the shell's catch-all,
 * which renders the overview, and every role holds what the overview reads.
 */
export function sectionRefusedAt(pathname: string, role: Role): Section | null {
  const section = sectionOf(pathname);
  return section !== null && !reaches(role, section) ? section : null;
}

/** The idle window a path is measured against; the shortest one when nothing matches. */
export function idleClassOf(pathname: string): IdleClass {
  // An unrecognised path is treated as a gate screen rather than an administration one. The
  // failure directions are not symmetric: locking an admin screen after five minutes is an
  // annoyance, and leaving an unrecognised gate screen open for an hour is a shift attributed to
  // the wrong person.
  return sectionOf(pathname)?.idle ?? 'gate';
}
